/* libs */
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";
import firebase from "firebase";
import { isEmpty } from "lodash";

/* components */
import Page from "../../shared/Page";
import Switcher from "../../ui/Switcher";

/* helpers */
import * as appConfigActions from "../../../actions/appConfigActions";
import * as controlPanelActions from "../../../actions/controlPanelActions";
import * as globalActions from "../../../actions/globalActions";
import * as sessionsActions from "../../../actions/sessionsActions";
import parseAppKey from "../../../helpers/routeHelpers/parseAppKey";

const COLLECTION = "questions";

const switcherOptions = [{ label: "Recent", value: "recent" }, { label: "Popular", value: "popular" }];

class SessionsPage extends Component {
	constructor(props) {
		super(props);
		this.appKey = this.props.match.params.appKey;
		this.state = {
			data: {},
			popularData: {},
			filter: "recent",
			projectFirebase: null,
		};
	}

	componentDidMount() {
		const {
			apps,
			sessions: { sessions },
			appConfig: { appConfig },
			appConfigActions,
			sessionsActions,
		} = this.props;

		if (isEmpty(appConfig)) {
			appConfigActions.loadAppConfig(this.appKey);
		}
		sessionsActions.loadSessions(this.appKey);

		let appId = apps ? apps.find(app => app.key === parseAppKey()).id : null;
		if (sessions.length && sessions[0].appId === appId) {
			this.subscribeToSessions();
		}
	}

	componentDidUpdate(prevProps) {
		const {
			sessions: { sessions: prevSessions },
		} = prevProps;
		const {
			sessions: { sessions: currentSessions },
		} = this.props;

		if (prevSessions.length === 0 && currentSessions.length) {
			this.subscribeToSessions();
		}
	}

	subscribeToSessions = () => {
		const {
			appConfig: { appConfig, appName },
			sessions: { sessions },
		} = this.props;
		if (!appConfig.webConfig) {
			console.error("Web Config is missing");
		} else {
			const { projectFirebase: firebaseFromState } = this.state;
			let projectFirebase = null;
			if (!firebaseFromState) {
				const initializedAppApiKeys = firebase.apps.map(a => a["options_"].apiKey);
				const webConfig = JSON.parse(appConfig.webConfig);
				const { apiKey: apiKeyFromConfig } = webConfig;

				// if an existing firebase instance has the same API key, we will use that.
				// if that doesn't exist yet, we need to create a new one

				if (initializedAppApiKeys.includes(apiKeyFromConfig)) {
					projectFirebase = firebase.apps.find(a => a["options_"].apiKey === apiKeyFromConfig);
				} else {
					// we need to check and see if any other firebase instances of this app have been created yet.
					// if we have others, we need to auto-increment the firebase instance name so we don't get an
					//   error about the app already being initialized
					const allFirebaseNames = firebase.apps.map(a => a["name_"]).filter(a => a !== "[DEFAULT]");
					if (isEmpty(allFirebaseNames)) {
						projectFirebase = firebase.initializeApp(webConfig, `${appName}-firebase1`);
					} else {
						const newestInstance = allFirebaseNames
							.filter(a => a.includes(appName))
							.sort()
							.splice(-1)
							.pop();
						const newestId = parseInt(newestInstance.replace(/(\S+)(-firebase(\d+))/g, "$3"));
						const newId = newestId + 1;
						projectFirebase = firebase.initializeApp(webConfig, `${appName}-firebase${newId}`);
					}
				}
			} else {
				projectFirebase = firebaseFromState;
			}
			this.setState({ projectFirebase });

			const db = projectFirebase.firestore();
			db.settings({ timestampsInSnapshots: true });

			sessions.forEach(session => {
				const queryNew = db
					.collection(COLLECTION)
					.where("sessionId", "==", session.id)
					.orderBy("flags")
					.orderBy("timestamp", "desc");

				const queryPopular = db
					.collection(COLLECTION)
					.where("sessionId", "==", session.id)
					.orderBy("flags")
					.orderBy("votes", "desc");

				queryNew.onSnapshot(querySnapshot => {
					let questions = [];
					querySnapshot.forEach(doc => {
						questions.push(Object.assign({}, doc.data(), { docId: doc.ref.id }));
					});
					if (questions.length) {
						this.setState({ data: Object.assign({}, this.state.data, { [session.id]: questions }) });
					}
				});

				queryPopular.onSnapshot(querySnapshot => {
					let questions = [];
					querySnapshot.forEach(doc => {
						questions.push(Object.assign({}, doc.data(), { docId: doc.ref.id }));
					});
					if (questions.length) {
						this.setState({
							popularData: Object.assign({}, this.state.popularData, { [session.id]: questions }),
						});
					}
				});
			});
		}
	};

	getAppId = key => {
		const { apps } = this.props;
		const app = apps.find(a => a.key === key);
		return app.id;
	};

	loadSessions = appId => {
		this.props.sessionsActions.loadSessions(appId);
	};

	banQuestion = docId => {
		const { projectFirebase } = this.state;
		if (window.confirm("Are you sure you want to remove this question?")) {
			const db = projectFirebase.firestore();
			db.collection(COLLECTION)
				.doc(docId)
				.update({ flags: 100 });
		}
	};
	restoreQuestion = docId => {
		const { projectFirebase } = this.state;
		if (window.confirm("Are you sure you want to restore this question?")) {
			const db = projectFirebase.firestore();
			db.collection(COLLECTION)
				.doc(docId)
				.update({ flags: 0 });
		}
	};

	changeFilter = (index, option) => {
		this.setState({ filter: option.value });
	};

	render() {
		const { sessions } = this.props.sessions;
		const data = this.state.filter === "recent" ? this.state.data : this.state.popularData;

		return (
			<Page title="Q&A Manager">
				{Object.keys(data).length === 0 ? (
					<div className="access-denied">
						<div className="absolute-center ta-center">
							<div className="access-denied-icon">
								<i className="fal fa-map-marker-question"></i>
							</div>
							<div className="access-denied-text">{"No Q&A Sessions Created Yet"}</div>
						</div>
					</div>
				) : (
					<div>
						<Switcher
							initialValue={this.state.filter}
							options={switcherOptions}
							onSelect={this.changeFilter}
						/>
						<div className="qa-manager scroll-horizontal">
							{sessions.map(session => {
								if (data[session.id]) {
									return (
										<div key={session.id} className="qa-session">
											<h4 className="page-title">{session.title}</h4>
											<div className="scroll-vertical">
												{data[session.id].map((question, index) => {
													return (
														<div key={index} className="qa-question">
															{question.flags < 3 ? (
																<div>
																	<div className="pull-right">
																		<div
																			className="btn btn-danger"
																			onClick={() => {
																				this.banQuestion(question.docId);
																			}}
																		>
																			<i className="fal fa-ban"></i>
																		</div>
																	</div>
																	<div className="qa-question-text">
																		{question.question}
																	</div>
																	<div style={{ marginTop: "10px" }}>
																		<span style={{ marginRight: "10px" }}>
																			<i className="fal fa-thumbs-up"></i>{" "}
																			{question.votes}
																		</span>{" "}
																		<i className="fal fa-flag"></i> {question.flags}
																	</div>
																</div>
															) : (
																<div>
																	<div className="pull-right">
																		<div
																			className="btn btn-default"
																			onClick={() => {
																				this.restoreQuestion(question.docId);
																			}}
																		>
																			<i className="fal fa-undo"></i>
																		</div>
																	</div>
																	<div className="qa-question-text disabled">
																		{question.question}
																	</div>
																</div>
															)}
														</div>
													);
												})}
											</div>
										</div>
									);
								} else return "";
							})}
						</div>
					</div>
				)}
			</Page>
		);
	}
}

SessionsPage.propTypes = {
	actions: PropTypes.any,
	appConfig: PropTypes.object,
	appConfigActions: PropTypes.object,
	apps: PropTypes.array,
	controlPanelActions: PropTypes.object,
	globalActions: PropTypes.any,
	history: PropTypes.object,
	match: PropTypes.any,
	pagesActions: PropTypes.object,
	sessions: PropTypes.object,
	sessionsActions: PropTypes.object,
	topics: PropTypes.array,
	topicsActions: PropTypes.object,
};

const mapStateToProps = state => {
	const qaSessions = [];

	state.sessions?.sessions?.forEach(session => {
		if (session.kind === "full") {
			qaSessions.push(session);
			session.childItems?.forEach(childSession => {
				if (childSession.kind === "full") {
					qaSessions.push(childSession);
				}
			});
		}
	});
	return {
		appConfig: state.appConfig,
		apps: state.apps.apps,
		sessions: { ...state.sessions, sessions: qaSessions },
		topics: state.topics.topics,
	};
};

const mapDispatchToProps = dispatch => ({
	appConfigActions: bindActionCreators(appConfigActions, dispatch),
	controlPanelActions: bindActionCreators(controlPanelActions, dispatch),
	globalActions: bindActionCreators(globalActions, dispatch),
	sessionsActions: bindActionCreators(sessionsActions, dispatch),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(SessionsPage);
